import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  ADMIN_TIME_CARD_CHANGE_REQUESTS_QUERY_KEY,
  fetchAdminPendingTimeCardChangeRequests,
  formatMiscTimeCardActionLabel,
  formatMiscTimeCardInstant,
  postAdminApproveTimeCardChangeRequest,
  postAdminRejectTimeCardChangeRequest,
  type TimeCardChangeRequest,
} from "@/lib/miscQuickBooksTimeCardsApi";

const PAGE_HELP =
  "Review operator time card correction requests. Approving applies the change to QuickBooks Time or hub break records. Rejecting leaves the original times unchanged. Pending requests stay in this queue until you approve or reject them.";

function RequestDetail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{value}</dd>
    </div>
  );
}

function PendingRequestCard({
  request,
  busy,
  onApprove,
  onReject,
}: {
  request: TimeCardChangeRequest;
  busy: boolean;
  onApprove: (notes: string) => void;
  onReject: (notes: string) => void;
}) {
  const [notes, setNotes] = useState("");

  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-medium text-slate-900">
              {request.subjectUserLabel ?? request.subjectUserId}
            </p>
            <p className="text-sm text-muted-foreground">
              {formatMiscTimeCardActionLabel(request.action)} · {request.calendarDate}
            </p>
          </div>
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900">
            Pending
          </span>
        </div>

        <dl className="grid gap-3 sm:grid-cols-2">
          <RequestDetail label="Requested by" value={request.requestedByLabel ?? "—"} />
          <RequestDetail
            label="Requested at"
            value={formatMiscTimeCardInstant(request.requestedAt)}
          />
          <RequestDetail label="Reason" value={request.reason} />
          {request.applyError != null ? (
            <RequestDetail label="Last apply error" value={request.applyError} />
          ) : null}
        </dl>

        {(request.originalPayload.start != null || request.proposedPayload.start != null) && (
          <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm sm:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Original
              </p>
              <p className="mt-1">
                {formatMiscTimeCardInstant(
                  typeof request.originalPayload.start === "string"
                    ? request.originalPayload.start
                    : null,
                )}{" "}
                →{" "}
                {formatMiscTimeCardInstant(
                  typeof request.originalPayload.end === "string" ? request.originalPayload.end : null,
                )}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Proposed
              </p>
              <p className="mt-1">
                {formatMiscTimeCardInstant(
                  typeof request.proposedPayload.start === "string"
                    ? request.proposedPayload.start
                    : null,
                )}{" "}
                →{" "}
                {formatMiscTimeCardInstant(
                  typeof request.proposedPayload.end === "string" ? request.proposedPayload.end : null,
                )}
              </p>
            </div>
          </div>
        )}

        <div>
          <Label htmlFor={`admin-time-card-notes-${request.id}`}>Admin notes (optional)</Label>
          <TextArea
            id={`admin-time-card-notes-${request.id}`}
            className="mt-1.5 min-h-[72px]"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={busy} onClick={() => onApprove(notes.trim())}>
            Approve
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => onReject(notes.trim())}
          >
            Reject
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export function AdminTimeCardChangesPage() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const pendingQuery = useQuery({
    queryKey: ADMIN_TIME_CARD_CHANGE_REQUESTS_QUERY_KEY,
    queryFn: fetchAdminPendingTimeCardChangeRequests,
    refetchInterval: 60_000,
  });

  const approveMutation = useMutation({
    mutationFn: postAdminApproveTimeCardChangeRequest,
    onSuccess: async () => {
      setMessage({ type: "success", text: "Request approved and applied." });
      await queryClient.invalidateQueries({ queryKey: ADMIN_TIME_CARD_CHANGE_REQUESTS_QUERY_KEY });
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Approve failed." });
      void queryClient.invalidateQueries({ queryKey: ADMIN_TIME_CARD_CHANGE_REQUESTS_QUERY_KEY });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: postAdminRejectTimeCardChangeRequest,
    onSuccess: async () => {
      setMessage({ type: "success", text: "Request rejected." });
      await queryClient.invalidateQueries({ queryKey: ADMIN_TIME_CARD_CHANGE_REQUESTS_QUERY_KEY });
    },
    onError: (error: Error) => {
      setMessage({ type: "error", text: error.message || "Reject failed." });
    },
  });

  const busy = approveMutation.isPending || rejectMutation.isPending;
  const requests = pendingQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Time card changes</PageTitleWithHelp>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={pendingQuery.isFetching}
          onClick={() => void pendingQuery.refetch()}
        >
          {pendingQuery.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden />
          )}
          Refresh
        </Button>
      </div>

      {message != null ? (
        <p
          className={cn(
            "text-sm",
            message.type === "success" ? "text-emerald-700" : "text-destructive",
          )}
        >
          {message.text}
        </p>
      ) : null}

      {pendingQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading pending requests…
        </div>
      ) : pendingQuery.isError ? (
        <p className="text-sm text-destructive">Failed to load pending requests.</p>
      ) : requests.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-muted-foreground">No pending time card change requests.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((request) => (
            <PendingRequestCard
              key={request.id}
              request={request}
              busy={busy}
              onApprove={(notes) => {
                setMessage(null);
                approveMutation.mutate({
                  requestId: request.id,
                  resolutionNotes: notes || null,
                });
              }}
              onReject={(notes) => {
                setMessage(null);
                rejectMutation.mutate({
                  requestId: request.id,
                  resolutionNotes: notes || null,
                });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
